import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../../lib/api";
import { Header } from "../../components/vc/Header";
import { SearchBar } from "../../components/vc/SearchBar";
import { EmptyState, Section, SkeletonList } from "../../components/vc/layout";
import { PrimaryButton, SecondaryButton } from "../../components/vc/Buttons";
import { FloatingButton } from "../../components/vc/FloatingButton";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import {
  Users, Plus, KeyRound, Copy, MessageCircle, Send, X, UserPlus, ArrowUpRight, ClipboardList, Sparkles, ChevronRight, Package,
} from "lucide-react";
import { toast } from "sonner";
import { buildWhatsAppUrl } from "../../lib/phone";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { AdminTaskDetailSheet } from "../../components/vc/AdminTaskDetailSheet";

const DEPARTMENTS = ["Sales", "Delivery", "Warehouse", "Accounts", "Purchase", "Support", "Other"];

// Phase 5 #2 — priority-based sticky note appearance, same visual language
// as the employee's own Home sticky notes.
const PRIORITY_STYLE = {
  urgent: { bg: "bg-red-50", border: "border-red-300", text: "text-red-700", pin: "bg-red-500", label: "Urgent" },
  high: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-700", pin: "bg-amber-500", label: "High" },
  medium: { bg: "bg-sky-50", border: "border-sky-300", text: "text-sky-700", pin: "bg-sky-500", label: "Medium" },
  low: { bg: "bg-slate-50", border: "border-slate-300", text: "text-slate-600", pin: "bg-slate-400", label: "Low" },
};

function TaskSticky({ task, tilt, onOpen }) {
  const p = PRIORITY_STYLE[task.priority] || PRIORITY_STYLE.medium;
  const statusLabel = task.status === "in_progress" ? "Ongoing" : "Pending";
  return (
    <button
      type="button"
      onClick={() => onOpen(task.id)}
      data-testid={`admin-sticky-${task.id}`}
      className={`relative text-left block rounded-2xl border-2 ${p.border} ${p.bg} p-3.5 pt-4 shadow-md hover:shadow-lg transition active:scale-[0.97] ${tilt}`}
    >
      <span className={`absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full ${p.pin} shadow`} aria-hidden />
      <div className="flex items-center justify-between gap-1 mb-1.5">
        <span className={`text-[10px] font-bold uppercase tracking-wider ${p.text}`}>{p.label}</span>
        <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${task.status === "in_progress" ? "bg-white/70 text-amber-700" : "bg-white/70 text-slate-500"}`}>
          {statusLabel}
        </span>
      </div>
      <div className="font-bold text-slate-900 text-sm leading-snug truncate" data-testid={`admin-sticky-employee-${task.id}`}>
        {task.employee_name || "Unassigned"}
      </div>
      {(task.client_name || task.vendor_name) && (
        <div className="text-xs text-slate-600 leading-snug mt-0.5 truncate" data-testid={`admin-sticky-party-${task.id}`}>
          {task.vendor_name ? `Vendor: ${task.vendor_name}` : `Client: ${task.client_name}`}
        </div>
      )}
      <div className="text-[11px] font-semibold text-slate-500 flex items-center gap-1 mt-1.5">
        <Package className="w-3 h-3" /> {task.item_count} item{task.item_count === 1 ? "" : "s"}
      </div>
    </button>
  );
}

export default function VCEmployees() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [boardTasks, setBoardTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [invite, setInvite] = useState(null); // {employee, code, accept_url}
  const [openTaskId, setOpenTaskId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", role: "", department: "Sales", email: "", active: true, designation: "",
  });

  const load = () => {
    setLoading(true);
    Promise.all([
      apiClient.get("/employees").then((r) => r.data).catch(() => []),
      apiClient.get("/tasks/board").then((r) => r.data).catch(() => []),
    ]).then(([emps, board]) => {
      setEmployees(emps || []);
      setBoardTasks(board || []);
    }).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const decorated = useMemo(() => employees.map((e) => ({
    id: e.id,
    name: e.name,
    role: e.designation || e.role || "Staff",
    phone: e.phone || "",
    // "Active" here means: has accepted invite AND owner hasn't marked them inactive.
    active: !!e.has_account && (e.status || "active") !== "inactive",
    inactive_by_owner: (e.status || "active") === "inactive",
    email: e.email,
    employee_code: e.employee_code,
    profile_photo: e.profile_photo,
  })), [employees]);

  const filteredEmployees = useMemo(() => (
    !q.trim() ? decorated : decorated.filter((e) => `${e.name} ${e.role}`.toLowerCase().includes(q.toLowerCase()))
  ), [decorated, q]);

  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: typeof e === "string" ? e : e.target?.value ?? e }));

  const submit = async () => {
    if (!form.name.trim() || !form.role.trim()) {
      toast.error("Name and role are required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await apiClient.post("/employees", {
        name: form.name.trim(),
        role: form.role.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        designation: (form.designation || form.role).trim(),
      });
      toast.success("Employee added");
      setEmployees((arr) => [data, ...arr]);
      // If phone provided, immediately generate invite for the WhatsApp flow
      if (form.phone.trim()) {
        try {
          const { data: inv } = await apiClient.post(`/employees/${data.id}/invite`);
          setInvite({ employee: data, ...inv });
        } catch { /* ignore */ }
      }
      setAddOpen(false);
      setForm({ name: "", phone: "", role: "", department: "Sales", email: "", active: true, designation: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  const whatsappInvite = () => {
    if (!invite) return;
    const msg = `Hi ${invite.employee.name}! Join KonexoCRM as ${invite.employee.role}. Link: ${invite.accept_url} · Code: ${invite.code}`;
    const url = buildWhatsAppUrl(invite.employee.phone, msg);
    if (!url) { toast.error("This employee's phone number looks invalid"); return; }
    window.open(url, "_blank");
  };

  return (
    <>
      <Header />
      <div className="px-4 pt-4 pb-36 space-y-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 leading-tight">Manage Employees</h2>
          <p className="text-sm text-slate-500 mt-0.5">{decorated.length} in your team</p>
        </div>

        {/* Add New Task hero card — kept exactly as-is */}
        <button
          onClick={() => navigate("/tasks/assign")}
          data-testid="add-new-task-hero"
          className="w-full text-left relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 text-white p-5 shadow-lg shadow-emerald-500/20 hover:shadow-xl hover:-translate-y-0.5 transition-all group"
        >
          <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/10" />
          <div className="absolute -right-4 bottom-0 w-24 h-24 rounded-full bg-white/10" />
          <div className="relative flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur grid place-items-center shrink-0">
              <ClipboardList className="w-7 h-7" strokeWidth={2.4} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest opacity-90">
                <Sparkles className="w-3 h-3" /> New Task
              </div>
              <div className="text-lg font-bold leading-tight mt-1">Add New Task</div>
              <div className="text-xs opacity-80 mt-0.5">Excel · Image · Manual notes</div>
            </div>
            <ArrowUpRight className="w-6 h-6 opacity-80 group-hover:rotate-45 transition-transform" />
          </div>
        </button>

        {/* Pending + Ongoing tasks as sticky notes (Phase 5 #2) */}
        <Section title="Team Tasks" subtitle={`${boardTasks.length} pending/ongoing`} testid="team-tasks-section">
          {loading ? (
            <div className="grid grid-cols-2 gap-3">{[1, 2].map((i) => <SkeletonList key={i} count={1} height="h-28" />)}</div>
          ) : boardTasks.length === 0 ? (
            <EmptyState Icon={ClipboardList} title="No pending tasks" subtitle="All caught up — great work!" />
          ) : (
            <div className="grid grid-cols-2 gap-3" data-testid="admin-sticky-grid">
              {boardTasks.map((t, i) => (
                <TaskSticky key={t.id} task={t} tilt={i % 2 === 0 ? "-rotate-1" : "rotate-1"} onOpen={setOpenTaskId} />
              ))}
            </div>
          )}
        </Section>

        <SearchBar value={q} onChange={setQ} placeholder="Search employees" testid="employees-search" />

        {/* Employee list — compact, photo + name only (Phase 5 #3) */}
        <Section
          title="Employees"
          subtitle={`${filteredEmployees.length} shown`}
          testid="employee-list-section"
          to="/employees/deleted"
          actionLabel="Deleted"
          linkTestId="deleted-employees-link"
        >
          {loading ? <SkeletonList height="h-16" /> : filteredEmployees.length === 0 ? (
            <EmptyState
              Icon={Users}
              title={q ? "No matches" : "No employees yet"}
              subtitle={q ? "Try a different search" : "Add someone and send them the invite link"}
            />
          ) : (
            <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-100 overflow-hidden">
              {filteredEmployees.map((e) => (
                <EmployeeListRow key={e.id} employee={e} onClick={() => navigate(`/employees/${e.id}`)} />
              ))}
            </div>
          )}
        </Section>
      </div>

      <FloatingButton
        Icon={UserPlus}
        onClick={() => setAddOpen(true)}
        label="Add employee"
        testid="employees-fab"
      />

      {/* Add Employee bottom sheet */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle>Add Employee</DialogTitle>
            <DialogDescription>Fill in the details — send an invite via WhatsApp after saving.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Name *</Label>
              <Input data-testid="emp-form-name" value={form.name} onChange={setF("name")} placeholder="Full name" className="h-12 rounded-xl" />
            </div>
            <div className="space-y-1.5">
              <Label>Phone (for WhatsApp)</Label>
              <Input data-testid="emp-form-phone" value={form.phone} onChange={setF("phone")} type="tel" placeholder="Optional" className="h-12 rounded-xl" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label>Role *</Label>
                <Input data-testid="emp-form-role" value={form.role} onChange={setF("role")} placeholder="Delivery, Sales..." className="h-12 rounded-xl" />
              </div>
              <div className="space-y-1.5">
                <Label>Department</Label>
                <Select value={form.department} onValueChange={setF("department")}>
                  <SelectTrigger data-testid="emp-form-department" className="h-12 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Designation (optional)</Label>
              <Input data-testid="emp-form-designation" value={form.designation} onChange={setF("designation")} placeholder="Senior Executive, Team Lead..." className="h-12 rounded-xl" />
            </div>
            <div className="space-y-1.5">
              <Label>Email (optional)</Label>
              <Input data-testid="emp-form-email" value={form.email} onChange={setF("email")} type="email" placeholder="For desktop login" className="h-12 rounded-xl" />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <div className="text-xs font-semibold text-slate-500">Status</div>
              <button
                onClick={() => setF("active")(!form.active)}
                data-testid="emp-form-status"
                className={`h-8 px-3 rounded-full text-xs font-bold transition ${
                  form.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
                }`}
              >
                {form.active ? "Active" : "Inactive"}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 pt-2">
              <SecondaryButton onClick={() => setAddOpen(false)} data-testid="emp-form-cancel" className="flex items-center justify-center gap-1">
                <X className="w-4 h-4" /> Cancel
              </SecondaryButton>
              <PrimaryButton onClick={submit} loading={saving} data-testid="emp-form-submit" className="flex items-center justify-center gap-1">
                <Send className="w-4 h-4" /> Invite Employee
              </PrimaryButton>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite share dialog */}
      <Dialog open={!!invite} onOpenChange={(o) => !o && setInvite(null)}>
        <DialogContent className="max-w-sm rounded-3xl">
          <DialogHeader>
            <DialogTitle>Invite {invite?.employee.name}</DialogTitle>
            <DialogDescription>Share this link or code. Expires in 7 days.</DialogDescription>
          </DialogHeader>
          {invite && (
            <div className="space-y-3">
              <div data-testid="invite-share-link" className="bg-slate-50 rounded-xl p-3 text-xs font-mono break-all border border-slate-200">
                {invite.accept_url}
              </div>
              <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-xl p-3">
                <KeyRound className="w-4 h-4 text-indigo-700" />
                <span data-testid="invite-share-code" className="font-mono font-bold text-indigo-900 flex-1">{invite.code}</span>
                <button onClick={() => copyText(invite.code)} data-testid="invite-share-copy-code" className="text-indigo-700 hover:text-indigo-900">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <SecondaryButton onClick={() => copyText(invite.accept_url)} data-testid="invite-share-copy-link" className="flex items-center justify-center gap-1">
                  <Copy className="w-4 h-4" /> Copy link
                </SecondaryButton>
                <PrimaryButton onClick={whatsappInvite} data-testid="invite-share-wa" className="flex items-center justify-center gap-1">
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </PrimaryButton>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AdminTaskDetailSheet
        taskId={openTaskId}
        open={!!openTaskId}
        onOpenChange={(o) => !o && setOpenTaskId(null)}
        onUpdated={() => load()}
      />
    </>
  );
}

function EmployeeListRow({ employee, onClick }) {
  const initial = (employee.name || "?").charAt(0).toUpperCase();
  const ring = employee.active ? "ring-emerald-400" : employee.inactive_by_owner ? "ring-slate-300" : "ring-amber-400";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`employee-row-${employee.id}`}
      className="w-full flex items-center gap-3 p-3.5 hover:bg-slate-50 active:bg-slate-100 transition text-left"
    >
      {employee.profile_photo ? (
        <img src={employee.profile_photo} alt={employee.name} className={`w-11 h-11 rounded-full object-cover shrink-0 ring-2 ${ring}`} />
      ) : (
        <div className={`w-11 h-11 rounded-full bg-gradient-to-br from-indigo-100 to-indigo-200 text-indigo-700 grid place-items-center shrink-0 font-bold ring-2 ${ring}`}>
          {initial}
        </div>
      )}
      <div className="min-w-0 flex-1 font-semibold text-slate-900 truncate">{employee.name}</div>
      <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
    </button>
  );
}

